import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, CheckCircle2, ListTodo } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface SpendingHabit {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  is_completed: boolean;
  category: string | null;
  created_at: string;
  completed_at: string | null;
}

const DEFAULT_HABITS = [
  "Review all subscriptions and cancel unused ones",
  "Set up automatic savings transfer",
  "Create a weekly grocery budget",
  "Wait 24 hours before impulse purchases over $50",
  "Pack lunch instead of eating out",
  "Review and negotiate monthly bills",
  "Track every expense for one week",
  "Set up emergency fund goal",
];

export const SpendingHabitsChecklist = () => {
  const [habits, setHabits] = useState<SpendingHabit[]>([]);
  const [newHabit, setNewHabit] = useState("");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchHabits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("spending_habits")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setHabits(data || []);
    } catch (error) {
      console.error("Error fetching habits:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHabits();
  }, []);

  const addHabit = async (title: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("spending_habits")
        .insert({ user_id: user.id, title });

      if (error) throw error;
      setNewHabit("");
      fetchHabits();
      toast({ title: "Habit added! 🎯" });
    } catch (error) {
      console.error("Error adding habit:", error);
      toast({ title: "Error", description: "Failed to add habit", variant: "destructive" });
    }
  };

  const toggleHabit = async (habit: SpendingHabit) => {
    try {
      const { error } = await supabase
        .from("spending_habits")
        .update({ 
          is_completed: !habit.is_completed,
          completed_at: !habit.is_completed ? new Date().toISOString() : null
        })
        .eq("id", habit.id);

      if (error) throw error;
      fetchHabits();
      
      if (!habit.is_completed) {
        toast({ title: "Great job! 🎉", description: "Keep building those healthy habits!" });
      }
    } catch (error) {
      console.error("Error toggling habit:", error);
    }
  };

  const deleteHabit = async (id: string) => {
    try {
      const { error } = await supabase
        .from("spending_habits")
        .delete()
        .eq("id", id);

      if (error) throw error;
      fetchHabits();
    } catch (error) {
      console.error("Error deleting habit:", error);
    }
  };

  const completedCount = habits.filter(h => h.is_completed).length;
  const progress = habits.length > 0 ? (completedCount / habits.length) * 100 : 0;

  return (
    <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
      {/* Gradient top border on hover */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="pb-3 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ListTodo className="h-4 w-4 text-primary-text" />
              </div>
              Spending Habits
            </CardTitle>
            <CardDescription className="text-sm">
              Build healthy financial habits one step at a time
            </CardDescription>
          </div>
          {habits.length > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold font-mono-numbers">{completedCount}/{habits.length}</p>
              <p className="text-xs text-muted-foreground">completed</p>
            </div>
          )}
        </div>
        {habits.length > 0 && (
          <div className="w-full bg-muted/50 rounded-full h-2.5 mt-3">
            <motion.div 
              className="bg-gradient-to-r from-primary to-secondary h-2.5 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {habits.length === 0 && !loading && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">Start with some suggested habits:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {DEFAULT_HABITS.slice(0, 4).map((habit) => (
                <Button
                  key={habit}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => addHabit(habit)}
                >
                  + {habit.substring(0, 30)}...
                </Button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence>
          {habits.map((habit) => (
            <motion.div
              key={habit.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 ${
                habit.is_completed 
                  ? "bg-primary/5 border-primary/20" 
                  : "bg-muted/30 border-border/30 hover:border-primary/30"
              }`}
            >
              <Checkbox
                checked={habit.is_completed}
                onCheckedChange={() => toggleHabit(habit)}
              />
              <span className={`flex-1 text-sm ${habit.is_completed ? "line-through text-muted-foreground" : ""}`}>
                {habit.title}
              </span>
              {habit.is_completed && <CheckCircle2 className="h-4 w-4 text-income-text" />}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => deleteHabit(habit.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </motion.div>
          ))}
        </AnimatePresence>

        <div className="flex gap-2 pt-3">
          <Input
            placeholder="Add a new habit..."
            value={newHabit}
            onChange={(e) => setNewHabit(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && newHabit && addHabit(newHabit)}
            className="text-sm bg-muted/30 border-border/50 focus:border-primary/50"
          />
          <Button 
            onClick={() => newHabit && addHabit(newHabit)}
            disabled={!newHabit}
            size="icon"
            className="bg-gradient-to-r from-primary to-secondary hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
